import React, { useState } from 'react';
import {
  Button,
  TextField,
  SelectField,
  Box,
  TagsForm,
  Heading,
  RadioGroup,
  Radio,
} from 'components';
import {
  getCitiesFromMarkets,
  getRolesFromMarkets,
  slugify,
  useTags,
  createCareerOppPost,
  decorateCareerOppPost,
  editCareerOppPost,
} from 'utils';
import DateFnsUtils from '@date-io/date-fns';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import { InputLabel, FormControlLabel } from '@material-ui/core';
import { addWeeks } from 'date-fns';
import CareerDevCard from 'components/freelancer/CareerDevCard';

const CAREER_DEV_INITIAL_STATE = {
  trainingType: '',
  company: '',
  cityId: '',
  location: 'in-person',
  startDate: new Date(),
  endDate: addWeeks(new Date(), 1),
  timeCommitmentAmount: '',
  timeCommitmentPeriod: 'days',
  timeCommitmentHours: '',
  timeCommitmentFrequency: 'day',
  roleId: 'any',
  demographic: 'open',
  skills: [],
  description: '',
  cost: '',
  url: '',
  email: '',
};

const TIME_COMMITMENT_PERIOD_CHOICES = [
  'Days',
  'Evenings',
  'Mornings',
  'Weeks',
  'Weekends',
  'Months',
];

const TIME_COMMITMENT_FREQUENCY_CHOICES = [
  'Day',
  'Week',
  'Evening',
  'Weekend',
  'Session',
];

const LOCATION_CHOICES = [
  { label: 'In person only', value: 'in-person' },
  { label: 'Virtual / Remote', value: 'remote' },
  { label: 'Hybrid In-person / Remote', value: 'hybrid' },
];

const CareerDevForm = ({
  defaultValues = CAREER_DEV_INITIAL_STATE,
  markets = {},
  user,
  history,
}) => {
  const [formState, _setFormState] = useState({
    email: user.email,
    ...defaultValues,
  });
  
  const setFormState = (key) => (v) =>
    _setFormState((formState) => ({ ...formState, [key]: v }));

  const [step, setStep] = useState(0);
  const [isPreview, setIsPreview] = useState(false);
  const cities = getCitiesFromMarkets(markets);
  const roles = getRolesFromMarkets(markets);
  const tags = useTags();

  const onPrev = () => setStep((s) => s - 1);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (step < 1) {
      setStep((s) => s + 1);
    } else if (formState.id) {
      if (!isPreview) return setIsPreview(true);
      await editCareerOppPost(formState, user);
      history.push('/career_dev/list');
    } else {
      if (!isPreview) return setIsPreview(true);
      await createCareerOppPost(formState, user);
      history.push('/career_dev/list');
    }
  };

  if (isPreview) {
    const post = decorateCareerOppPost(formState, cities, roles);

    return (
      <Box>
        <Heading>Check your post</Heading>
        <Box width={250} className="box-wrap" display="flex" mx="auto">
          <CareerDevCard careerDevPost={post} />
        </Box>
        <Box width={280} display="flex" mt={4} mx="auto">
          <Button
            variant="outlined"
            color="gray"
            onClick={() => setIsPreview(false)}
            style={{ flex: 1, marginRight: 16 }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSubmit}
            style={{ flex: 1 }}
          >
            Confirm
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Heading>{formState.id ? 'Edit' : 'Create New'} Career Development Post</Heading>

      <form onSubmit={onSubmit}>
        {step === 0 && (
          <>
            <TextField
              required
              value={formState.trainingType}
              onChange={(e) => setFormState('trainingType')(e.target.value)}
              name="trainingType"
              placeholder="Workshop, Apprenticeship, Certification, etc."
              label="Type of Training *"
            />
            <TextField
              required
              value={formState.company}
              onChange={(e) => setFormState('company')(e.target.value)}
              name="company"
              label="Company or Organization Offering It *"
              placeholder=""
            />
            
            <SelectField
              required
              name="city"
              label="Location *"
              value={formState.cityId}
              onChange={(e) => setFormState('cityId')(e.target.value)}
              choices={cities.map(({ id, label }) => ({
                label,
                value: id,
              }))}
            />

            <Box mt={2} mb={3}>
              <InputLabel shrink>Training Format *</InputLabel>
              <RadioGroup
                value={formState.location}
                onChange={(e) => setFormState('location')(e.target.value)}
              >
                {LOCATION_CHOICES.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio />}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            </Box>

            <Box display="flex" flex={1}>
              <KeyboardDatePicker
                required
                disableToolbar
                variant="inline"
                format="MM/dd/yyyy"
                margin="normal"
                id="start_date"
                label="Start Date *"
                value={formState.startDate}
                onChange={(v) => {
                  setFormState('startDate')(v);
                  if (v > formState.endDate) {
                    setFormState('endDate')(v);
                  }
                }}
              />
            </Box>
            
            <Box mt={2} mb={2}>
              <InputLabel shrink style={{ marginBottom: '8px' }}>Time Commitment *</InputLabel>
              <Box display="flex" mb={2}>
                <TextField
                  required
                  value={formState.timeCommitmentAmount}
                  onChange={(e) => setFormState('timeCommitmentAmount')(e.target.value)}
                  name="timeCommitmentAmount"
                  type="number"
                  wrapperProps={{ style: { flex: 1, marginRight: '8px' } }}
                />
                <SelectField
                  required
                  name="timeCommitmentPeriod"
                  value={formState.timeCommitmentPeriod}
                  onChange={(e) => setFormState('timeCommitmentPeriod')(e.target.value.toLowerCase())}
                  choices={TIME_COMMITMENT_PERIOD_CHOICES.map((period) => ({
                    label: period,
                    value: period.toLowerCase(),
                  }))}
                  wrapperProps={{ style: { flex: 1 } }}
                />
              </Box>
              
              <Box display="flex">
                <TextField
                  required
                  value={formState.timeCommitmentHours}
                  onChange={(e) => setFormState('timeCommitmentHours')(e.target.value)}
                  name="timeCommitmentHours"
                  type="number"
                  label="Hours per"
                  wrapperProps={{ style: { flex: 1, marginRight: '8px' } }}
                />
                <SelectField
                  required
                  name="timeCommitmentFrequency"
                  value={formState.timeCommitmentFrequency}
                  onChange={(e) => setFormState('timeCommitmentFrequency')(e.target.value.toLowerCase())}
                  choices={TIME_COMMITMENT_FREQUENCY_CHOICES.map((freq) => ({
                    label: freq,
                    value: freq.toLowerCase(),
                  }))}
                  wrapperProps={{ style: { flex: 1 } }}
                />
              </Box>
            </Box>

            <Box display="flex" flex={1}>
              <KeyboardDatePicker
                required
                disableToolbar
                variant="inline"
                format="MM/dd/yyyy"
                margin="normal"
                id="end_date"
                label="End Date *"
                value={formState.endDate}
                onChange={(v) => {
                  setFormState('endDate')(v);
                }}
              />
            </Box>
          </>
        )}

        {step === 1 && (
          <>
            <SelectField
              required
              name="role"
              label="Is the training specific to a Role? *"
              value={formState.roleId}
              onChange={(e) => setFormState('roleId')(e.target.value)}
              choices={[
                { label: 'Open to any Role', value: 'any' },
                ...roles.map(({ roleId, label }) => ({
                  label,
                  value: roleId,
                })),
              ]}
            />

            <SelectField
              required
              name="demographic"
              label="Is a specific demographic required? *"
              value={formState.demographic}
              onChange={(e) => setFormState('demographic')(e.target.value)}
              choices={[
                { label: 'Open to all', value: 'open' },
              ]}
            />

            <TagsForm
              search
              max={3}
              tags={tags}
              value={formState.skills}
              onChange={({ skills }) => setFormState('skills')(skills)}
              label="Necessary Skills (Optional - 3 Max)"
            />

            <TextField
              multiline
              rows={4}
              value={formState.description}
              onChange={(e) => setFormState('description')(e.target.value)}
              name="description"
              label="Description *"
              placeholder="Describe the training opportunity"
              required
            />

            <TextField
              value={formState.cost}
              onChange={(e) => setFormState('cost')(e.target.value)}
              name="cost"
              label="Cost (if applicable)"
              placeholder="e.g. $500"
            />

            <TextField
              value={formState.url}
              onChange={(e) => setFormState('url')(e.target.value)}
              name="url"
              label="URL for more info (if applicable)"
              placeholder="e.g. https://example.com/training"
            />

            <TextField
              required
              value={formState.email}
              onChange={(e) => setFormState('email')(e.target.value)}
              name="email"
              label="Contact Email *"
              placeholder=""
            />
          </>
        )}

        <Box display="flex" mt={4}>
          {step > 0 && (
            <Button
              variant="contained"
              color="gray"
              onClick={onPrev}
              style={{ flex: 1, marginRight: 16 }}
            >
              Prev
            </Button>
          )}

          <Button
            variant="contained"
            color="primary"
            type="submit"
            style={{ flex: 1 }}
          >
            {step < 1 ? 'Next' : 'Submit'}
          </Button>
        </Box>
      </form>
    </MuiPickersUtilsProvider>
  );
};

export default CareerDevForm;
